import React, {Component} from "react";
import {DateTime, TimeSpan} from "util/time";
import ToggleButton from "components/forms/ToggleButton";
import './index.scss';
import ProgressSpinner from "components/ProgressSpinner";
import {loadLandlordBookingsStats} from "services/bookings/landlords";
import {handleError} from "util/errors";
import {LandlordBookingEarningStats, LandlordBookingStats, LandlordBookingTotalsStats} from "services/entities/booking";
import {CurrencyAmount} from "util/currency";

interface BookingsStatsSectionState {
  selectedYears: number[];
  selectedMonths: number[];
  stats: LandlordBookingStats|null;
}

const ZERO_AMOUNT = new CurrencyAmount(0);
const ZERO_DURATION = new TimeSpan();
const EMPTY_STATS = {
  bookings: {
    count: 0,
    duration: ZERO_DURATION,
    subtotal: ZERO_AMOUNT,
    taxes: ZERO_AMOUNT,
    total: ZERO_AMOUNT,
    averageDuration: ZERO_DURATION,
    occupancyRate: 0,
    vacancyRate: 0,
  },
  deals: {
    count: 0,
    averageBookings: 0
  },
  revenue: {
    count: 0,
    duration: ZERO_DURATION,
    subtotal: ZERO_AMOUNT,
    taxes: ZERO_AMOUNT,
    total: ZERO_AMOUNT,
    averagePerMonth: ZERO_AMOUNT,
    averagePerWeek: ZERO_AMOUNT,
    averagePerDeal: ZERO_AMOUNT,
    averagePerBooking: ZERO_AMOUNT
  },
  landlordSplit: {
    subtotal: ZERO_AMOUNT,
    taxes: ZERO_AMOUNT,
    total: ZERO_AMOUNT
  },
  sookSplit: {
    subtotal: ZERO_AMOUNT,
    taxes: ZERO_AMOUNT,
    total: ZERO_AMOUNT
  }
};

export class BookingsStatsSection extends Component<{}, BookingsStatsSectionState> {
  state: BookingsStatsSectionState = {
    selectedYears: [],
    selectedMonths: [],
    stats: null
  };

  componentDidMount(): void {
    const currentDate = DateTime.now();
    this.setState({selectedYears: [currentDate.year], selectedMonths: [currentDate.month]},
      () => this.loadBookingStats());
  }

  render() {
    return <div className="booking-stats">
      { this.renderYears() }
      { this.renderMonths() }
      { this.renderStats() }
    </div>;
  }

  loadBookingStats() {
    if(this.state.selectedMonths.length === 0 || this.state.selectedYears.length === 0) {
      this.setState({stats: EMPTY_STATS});
    } else {
      loadLandlordBookingsStats({months: this.state.selectedMonths, years: this.state.selectedYears}).then(
        stats => this.setState({stats: stats}),
        ex => handleError('Failed loading stats', ex)
      );
    }
  }

  renderYears() {
    const selectedYears = this.state.selectedYears;
    const currentYear = DateTime.now().year;
    const minYear = currentYear - 1;
    const maxYear = currentYear + 1;
    const years = [];
    for(let k = minYear; k <= maxYear; k++) {
      years.push(k);
    }
    return <ul className="years">
      { years.map((year, i) => <li key={i}>
        <ToggleButton selected={selectedYears.indexOf(year) >= 0} allowDeselect={true}
                      onChange={selected => this.onYearSelectedChanged(year, selected)}>{year}</ToggleButton>
      </li>)}
    </ul>;
  }

  renderMonths() {
    const selectedMonths = this.state.selectedMonths;
    const months = [];
    for(let k = 0; k < 12; k++) {
      months.push(new DateTime(new Date(2000, k, 1)));
    }
    return <ul className="months">
      { months.map((dt, i) => <li key={i}>
        <ToggleButton selected={selectedMonths.indexOf(dt.month) >= 0} allowDeselect={true}
                      onChange={selected => this.onMonthSelectedChanged(dt.month, selected)}>{dt.format("MMM")}</ToggleButton>
      </li>)}
    </ul>;
  }

  renderStats() {
    const stats = this.state.stats;
    return <div className="stats">
      { this.renderStat('Bookings', stats?.bookings || null, stats?.bookings || null) }
      { this.renderStat('Revenue', stats?.revenue || null, stats?.revenue || null) }
      { this.renderStat('Landlord Split', stats?.landlordSplit || null) }
      { this.renderStat('Sook Split', stats?.sookSplit || null) }
    </div>;
  }

  renderStat(title: string, stat: LandlordBookingEarningStats|null, totalsStats: LandlordBookingTotalsStats|null = null) {
    return <div className={`stat ${!stat ? 'loading' : ''}`}>
      <div className="head">
        <h3>{ title }</h3>
        <span className="subtitle">{ totalsStats ? `${totalsStats.count} Bookings - ${Math.floor(totalsStats.duration.totalHours)} Hrs` : '' }</span>
      </div>
      <div className="body">
        { stat && <>
            <span className="subtotal">NET = { stat.subtotal.format("$0") }</span>
            <span className="taxes">VAT = { stat.taxes.format("$0") }</span>
            <span className="total">Total = { stat.total.format("$0") }</span>
        </> }
        { !stat && <ProgressSpinner /> }
      </div>
    </div>;
  }

  onYearSelectedChanged = (year: number, selected: boolean) => {
    const selectedYears = this.state.selectedYears;
    const index = selectedYears.indexOf(year);
    if(selected) {
      if(index < 0) {
        selectedYears.push(year);
        this.setState({selectedYears: selectedYears});
      }
    } if(index >= 0) {
      selectedYears.splice(index, 1);
      this.setState({selectedYears: selectedYears});
    }
    this.loadBookingStats();
  };

  onMonthSelectedChanged = (month: number, selected: boolean) => {
    const selectedMonths = this.state.selectedMonths;
    const index = selectedMonths.indexOf(month);
    if(selected) {
      if(index < 0) {
        selectedMonths.push(month);
        this.setState({selectedMonths: selectedMonths});
      }
    } if(index >= 0) {
      selectedMonths.splice(index, 1);
      this.setState({selectedMonths: selectedMonths});
    }
    this.loadBookingStats();
  };
}